import { useMemo, useState } from "react"
import { Link, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Badge, type BadgeTone } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { RowAvatar } from "@/components/domain/row-avatar"
import { FeedbackStackedBar } from "@/components/domain/feedback-stacked-bar"
import { AppuserRelatedAccounts } from "@/components/domain/appuser-related-accounts"
import { AppuserNotesPane } from "@/components/domain/appuser-notes-pane"
import { fmtCompactRelative, fmtNumber } from "@/lib/format"
import { cn } from "@/lib/cn"
import { choiceColorClasses } from "@/lib/choice-colors"
import type {
  AppuserConversation,
  AppuserDetail,
  AppuserSentimentSegment,
} from "@/types/appuser"
import type { FeedbackBarSegment } from "@/types/user_feedback"
import type { SharedProps } from "@/types/inertia"

// Wave 2a — read-only operator view of a single Appuser. All data is
// pre-shaped by Portal::Appusers::DetailPresenter; the page is dumb
// rendering. Layout: header strip + 2-column body (left = conversations,
// right = trust+safety + feedback + sentiment/cost + related accounts).
//
// Wave 2b owns <UserLink>; we use plain Inertia <Link> here so we don't
// step on it. Empty/null states are explicit so a brand-new user (zero
// convos, zero feedback) renders cleanly.

type Props = {
  appuser: AppuserDetail
}

const CONVO_FILTERS: Array<{ key: string; label: string; match: (c: AppuserConversation) => boolean }> = [
  { key: "all",        label: "All",         match: () => true },
  { key: "open",       label: "Open",        match: (c) => c.state === "open" },
  { key: "escalated",  label: "Escalated",   match: (c) => c.state === "escalated" },
  { key: "resolved",   label: "Resolved",    match: (c) => [
      "resolved", "auto_resolved", "assumed_resolved",
    ].includes(c.state) },
  { key: "rage_locked", label: "Rage-locked", match: (c) => c.state === "rage_locked" },
]

const STATE_TONE: Record<string, BadgeTone> = {
  open:             "neutral",
  escalated:        "warning",
  resolved:         "success",
  auto_resolved:    "success",
  assumed_resolved: "success",
  rage_locked:      "error",
  attempted_hack:   "error",
}

function stateTone(state: string): BadgeTone {
  return STATE_TONE[state] ?? "neutral"
}

function ratingGlyph(r: AppuserConversation["aiUserRating"]): string {
  if (r === "good") return "👍"
  if (r === "bad")  return "👎"
  return "—"
}

function headlineName(a: AppuserDetail): string {
  return a.combinedName ?? a.email ?? a.sourceKey
}

// Convert sentiment segments → the FeedbackStackedBar segment shape so we
// can reuse the existing bar primitive. Bar palette is by key; the
// sentiment keys aren't in the existing TONE map (which is feedback-keyed),
// so the bar will fall back to the muted neutral — that's intentional for
// v1; a sentiment-specific palette is a follow-up.
function asBarSegments(segments: AppuserSentimentSegment[]): FeedbackBarSegment[] {
  return segments.map((s) => ({
    key:   s.key,
    label: s.label,
    count: s.count,
    pct:   s.pct,
  }))
}

export default function AppusersShow({ appuser }: Props) {
  const [filterKey, setFilterKey] = useState<string>("all")
  const sharedProps      = usePage<SharedProps>().props
  const currentUserId    = sharedProps.current_user?.id ?? null
  const currentUserAdmin = sharedProps.isAdmin

  const filteredConvos = useMemo(() => {
    const filter = CONVO_FILTERS.find((f) => f.key === filterKey) ?? CONVO_FILTERS[0]
    return appuser.conversations.filter(filter.match)
  }, [appuser.conversations, filterKey])

  const tempLocked = appuser.lockoutStatus?.active === true && appuser.state !== "banned"

  return (
    <AppShell>
      <PageHeader
        eyebrow={<Link href="/tickets" className="hover:text-app-text">Tickets</Link>}
        title={headlineName(appuser)}
        subtitle={`Appuser #${appuser.id} · ${appuser.sourceKey}`}
      />

      {/* Header strip — stacks vertically on mobile (avatar + identity
          on top, counters below) and goes inline at sm:+ where there's
          room for all three blocks side-by-side. */}
      <Card className="flex flex-col gap-4 p-5 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex items-center gap-4 sm:contents">
          <RowAvatar name={headlineName(appuser)} src={appuser.avatarUrl} className="h-14 w-14 shrink-0 text-lg" />
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="min-w-0 truncate text-xl font-semibold text-app-text">
                {headlineName(appuser)}
              </h2>
              {appuser.vip && (
                <Badge tone="gold" title="VIP">★ VIP</Badge>
              )}
              {appuser.state === "banned" ? (
                <Badge tone="error">Banned</Badge>
              ) : tempLocked ? (
                <Badge tone="warning">Temp-locked</Badge>
              ) : (
                <Badge tone="success">Active</Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-app-muted">
              {appuser.email && <span className="break-all">{appuser.email}</span>}
              <Badge tone="neutral">{appuser.sourceKey}</Badge>
              {appuser.sourceCreatedAt
                ? <span title={`First seen by your system on ${appuser.sourceCreatedAt}`}>User since {fmtCompactRelative(appuser.sourceCreatedAt)}</span>
                : appuser.createdAt && <span>Created {fmtCompactRelative(appuser.createdAt)}</span>}
              {appuser.sourceState && (
                <SourceStateChip sourceState={appuser.sourceState} />
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-app-text sm:flex sm:flex-wrap sm:items-center">
          <Counter label="Convos"      value={fmtNumber(appuser.counters.convos)} />
          <Counter label="Escalations" value={fmtNumber(appuser.counters.escalations)} />
          <Counter label="Resolved"    value={fmtNumber(appuser.counters.resolved)} />
          <Counter label="Feedback"    value={fmtNumber(appuser.counters.feedback)} />
        </div>
      </Card>

      {/* Two-column body */}
      <section className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        {/* LEFT — Conversations */}
        <div className="space-y-4">
          <Card className="space-y-3 p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm font-medium text-app-text">Conversations</div>
              <div className="flex flex-wrap gap-2">
                {CONVO_FILTERS.map((f) => {
                  const active = filterKey === f.key
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setFilterKey(f.key)}
                      className={cn(
                        "inline-flex items-center rounded-chip border px-3 py-1 text-xs font-medium transition",
                        active
                          ? "border-brand-red/40 bg-brand-redSoft text-brand-red"
                          : "border-border bg-white/5 text-app-text hover:bg-white/10",
                      )}
                    >
                      {f.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {filteredConvos.length === 0 ? (
              <div className="text-sm text-app-muted">
                {appuser.conversations.length === 0
                  ? "No conversations yet."
                  : "No conversations match this filter."}
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {filteredConvos.map((c) => (
                  <li key={c.id} className="py-3 first:pt-0 last:pb-0">
                    <Link
                      href={`/tickets/${c.id}`}
                      className="block rounded-control px-1 py-1 transition hover:bg-white/5"
                    >
                      <div className="flex flex-wrap items-center gap-2 text-sm text-app-text">
                        <Badge tone={stateTone(c.state)}>{c.state}</Badge>
                        <span className="text-app-muted">{fmtCompactRelative(c.lastMessageAt)}</span>
                        <span className="text-app-subtle">·</span>
                        <span className="text-app-muted">{c.turnCount} turns</span>
                        <span className="text-app-subtle">·</span>
                        <span title="AI rating from this user">{ratingGlyph(c.aiUserRating)}</span>
                        {c.agentName && (
                          <>
                            <span className="text-app-subtle">·</span>
                            <span className="text-app-muted">{c.agentName}</span>
                          </>
                        )}
                      </div>
                      {(c.escalationReason || c.escalationReasonText) && (
                        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-app-muted">
                          {c.escalationReason && (
                            <span
                              className={`inline-flex items-center gap-1 rounded-control border border-status-error/30 bg-status-error/10 px-2 py-0.5 text-[11px] uppercase tracking-wide text-status-error ${c.escalationReason.status === "pending" ? "opacity-60" : ""}`}
                              title={c.escalationReason.description}
                            >
                              {c.escalationReason.name}
                              {c.escalationReason.status === "pending" && " (pending review)"}
                            </span>
                          )}
                          {c.escalationReasonText && (
                            <span className="truncate">Escalated: {c.escalationReasonText}</span>
                          )}
                        </div>
                      )}
                      {c.topics.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {c.topics.map((t) => (
                            <Badge key={t.key} tone="neutral">{t.name}</Badge>
                          ))}
                        </div>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* RIGHT — Trust & safety + feedback + sentiment + related accounts */}
        <div className="space-y-4">
          <Card className="space-y-3 p-5">
            <div className="text-sm font-medium text-app-text">Trust & safety</div>
            <div className="grid grid-cols-1 gap-3 text-xs sm:grid-cols-3">
              <SmallStat label="Rage warnings (30d)" value={appuser.trustSafety.rage30dWarnings} />
              <SmallStat label="Rage abuses (30d)"   value={appuser.trustSafety.rage30dAbuses} />
              <SmallStat label="Hack incidents (90d)" value={appuser.trustSafety.hack90dIncidents} />
            </div>

            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wide text-app-subtle">Lockout history (90d)</div>
              {appuser.trustSafety.lockoutHistory.length === 0 ? (
                <div className="text-sm text-app-muted">No lockouts in window.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-app-subtle">
                      <th className="py-1 pr-2">Kind</th>
                      <th className="py-1 pr-2">Severity</th>
                      <th className="py-1">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {appuser.trustSafety.lockoutHistory.map((row, i) => (
                      <tr key={i} className="text-app-text">
                        <td className="py-1 pr-2">{row.kind}</td>
                        <td className="py-1 pr-2">
                          <Badge tone={row.severity === "abuse" ? "error" : "warning"}>{row.severity}</Badge>
                        </td>
                        <td className="py-1 text-app-muted">{fmtCompactRelative(row.triggeredAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button variant="ghost" size="sm" disabled title="Coming soon">
                Ban user
              </Button>
              <Button variant="ghost" size="sm" disabled title="Coming soon">
                Lift lockout
              </Button>
            </div>
          </Card>

          <Card className="space-y-3 p-5">
            <div className="text-sm font-medium text-app-text">Feedback this user filed</div>
            {appuser.feedback.length === 0 ? (
              <div className="text-sm text-app-muted">No feedback filed.</div>
            ) : (
              <ul className="divide-y divide-border">
                {appuser.feedback.map((fb) => (
                  <li key={fb.id} className="py-2 first:pt-0 last:pb-0">
                    <Link
                      href={`/user_feedback?id=${fb.id}`}
                      className="block rounded-control px-1 py-1 transition hover:bg-white/5"
                    >
                      <div className="flex items-center gap-2">
                        <Badge tone="neutral">{fb.categoryLabel}</Badge>
                        <span className="truncate text-sm text-app-text">{fb.title}</span>
                      </div>
                      <div className="mt-1 text-xs text-app-muted">
                        {fmtCompactRelative(fb.createdAt)}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="space-y-3 p-5">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-app-text">Sentiment</div>
              <span className="text-xs text-app-subtle">{appuser.sentiment.window}</span>
            </div>
            {appuser.sentiment.totalMessages === 0 ? (
              <div className="text-sm text-app-muted">Not enough messages yet.</div>
            ) : (
              <FeedbackStackedBar segments={asBarSegments(appuser.sentiment.segments)} />
            )}
          </Card>

          {(appuser.emails.length > 0 || appuser.devices.length > 0) && (
            <Card className="space-y-3 p-5">
              <div className="text-sm font-medium text-app-text">Identity history</div>
              {appuser.emails.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs uppercase tracking-wide text-app-subtle">Emails</div>
                  <ul className="space-y-0.5 text-sm text-app-text">
                    {appuser.emails.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
              {appuser.devices.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs uppercase tracking-wide text-app-subtle">Devices</div>
                  <ul className="space-y-0.5 text-sm text-app-text">
                    {appuser.devices.map((d) => (
                      <li key={d.deviceId}>
                        <span className="text-app-muted">{d.deviceType}</span>{" "}
                        <span className="font-mono text-xs">{d.deviceId}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          )}

          <div id="notes">
            <AppuserNotesPane
              appuserId={appuser.id}
              notes={appuser.notes ?? []}
              currentUserId={currentUserId}
              currentUserAdmin={currentUserAdmin}
            />
          </div>

          <AppuserRelatedAccounts accounts={appuser.relatedAccounts} />
        </div>
      </section>
    </AppShell>
  )
}

function SourceStateChip({ sourceState }: { sourceState: NonNullable<AppuserDetail["sourceState"]> }) {
  const palette = choiceColorClasses(sourceState.color)
  const baseClasses = "inline-flex items-center rounded-chip border px-2 py-0.5 text-xs"
  const cls = palette
    ? `${baseClasses} ${palette.bg} ${palette.border} ${palette.text}`
    : `${baseClasses} border-border bg-app-panel/40 text-app-text`
  return (
    <span
      className={cls}
      title="System field. The value is set by your partner integration; operators can curate the choice list (and its colors) under Custom Fields."
    >
      <span className="text-app-subtle">sys&nbsp;·&nbsp;</span>
      {sourceState.label}
    </span>
  )
}

function Counter({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs uppercase tracking-wide text-app-subtle">{label}</span>
      <span className="font-medium text-app-text">{value}</span>
    </div>
  )
}

function SmallStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex flex-col rounded-control border border-border bg-white/5 p-2">
      <span className="text-[10px] uppercase tracking-wide text-app-subtle">{label}</span>
      <span className="mt-0.5 text-sm font-semibold text-app-text">{value}</span>
    </div>
  )
}
